import React, { useEffect, useRef, useState } from "react";
import { Flex, ProgressCircle, ToastQueue } from "@adobe/react-spectrum";
import { ListContactsContactRequest } from "../../../../../services/soap/main/requests/ListContactsContactRequest";
import { useDependency } from "../../../../../contexts/DependencyProvider";
import { useAsyncList } from "react-stately";
import CreatableSelect from "react-select/creatable";
import { components } from "react-select";
import { useDebounce } from "@uidotdev/usehooks";
import { truncateText } from "../../../../../utils/helperUtil";
import { useTranslation } from "react-i18next";
import styles from "./ContactsContactMultipleTag.module.css";

interface ContactsContactMultipleTagProps {
  createParams: (filterType: any, filterValue: any) => void;
  fieldName: string;
  specificType: string;
  setContactsSelected?: any[];
  populateEmails?: (emails: string[]) => void;
}

interface Contact {
  id: string;
  name: string;
  emailText: string;
  contactName: string;
  isEditing?: boolean; // Add a flag for editing state
  tempName?: string; // Add a temporary name for editing
}

const ContactsContactMultipleTag: React.FC<ContactsContactMultipleTagProps> = ({
  createParams,
  fieldName,
  specificType,
  setContactsSelected,
}) => {
  const { store, mainService } = useDependency();
  const { t } = useTranslation();
  const [selectedContacts, setSelectedContacts] = useState<any[]>(
    Array.isArray(setContactsSelected) ? setContactsSelected : [],
  );
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState("");
  const refSelectedContacts = useRef<any[]>([]);
  const debouncedInputValue = useDebounce(inputValue, 1000);

  const contactsList = useAsyncList({
    async load({ filterText }) {
      try {
        const content = filterText;
        // if(!content) {
        //   return { items: [] };
        // }
        let contactsResp = await mainService.listContactsContact(
          new ListContactsContactRequest(store.Server, store.SessionId, {
            limit: 20,
            offset: 0,
            specificType: specificType,
            content: content,
          }),
        );

        if (
          contactsResp &&
          contactsResp.count > 0 &&
          contactsResp.result === "OK"
        ) {
          const formattedArray = contactsResp.ROW.map((row: any) => ({
            id: String(row.id),
            name: `${row.content}`,
            contactName: `${row.contactName}`,
            emailText: `${row.content}`,
          }));
          return {
            items: formattedArray,
          };
        } else {
          return { items: [] };
        }
      } catch (error) {
        console.error("Error fetching contacts:", error);
        //ToastQueue.negative(`Error fetching contacts: ${error}`, { timeout: 3000 });
        return { items: [] };
      } finally {
        setShowLoader(false);
      }
    },
  });

  useEffect(() => {
    // Update the filter text in contactsList when the debounced input value changes
    contactsList.setFilterText(debouncedInputValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInputValue]);

  useEffect(() => {
    //console.log("setContactsSelected", setContactsSelected);
    //console.log('setContactsSelected', setContactsSelected);
    contactsList.setFilterText("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Validate email format
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle selection changes from react-select
  const handleChange = (newValue: any, actionMeta: any) => {
    // Convert react-select format to our internal format
    console.log("newValue", newValue);
    const newContacts = newValue.map((item: any) => {
      //console.log('item', item);
      return {
        id: item.value,
        name: item.label,
        contactName: item.contactName ?? item.label,
        emailText: item.emailText,
      };
    });

    refSelectedContacts.current = newContacts;
    setSelectedContacts(newContacts);

    // Update parent component with selected values
    const updateValue =
      newContacts.length > 0
        ? new Set(
            newContacts.map((item: any) => ({
              id: item.id,
              email: item.name,
              name: item.name,
              contactName: item.contactName,
              emailText: item.emailText,
            })),
          )
        : "";

    createParams(fieldName, updateValue);
  };

  // Handle creating new options
  const handleCreate = (inputValue: string) => {
    if (specificType === "email" && !isValidEmail(inputValue)) {
      ToastQueue.negative(
        t("please_enter_a_valid_email_address", { ns: "layout_components" }),
        { timeout: 3000 },
      );
      return;
    }

    // Create new contact
    const newContact = {
      id: crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).substring(2, 11),
      name: inputValue,
      contactName: inputValue,
      emailText: inputValue,
    };

    // Add to selected contacts
    const updatedContacts = [...selectedContacts, newContact];
    refSelectedContacts.current = updatedContacts;
    setSelectedContacts(updatedContacts);

    // Update parent component
    const updateValue =
      updatedContacts.length > 0
        ? new Set(
            updatedContacts.map((item: any) => ({
              id: item.id,
              email: item.name,
              name: item.name,
              contactName: item.contactName,
              emailText: item.emailText,
            })),
          )
        : "";

    createParams(fieldName, updateValue);
  };

  // Custom styles for react-select
  const customStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: "10px",
      fontSize: "12px",
      border: "none",
      height: "40px",
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: "transparent",
      borderRadius: "4px",
      margin: "2px",
      padding: "0",
    }),
    multiValueLabel: (base: any) => ({
      ...base,
      padding: "0",
    }),
    multiValueRemove: (base: any) => ({
      ...base,
      //display: 'none',
      backgroundColor: "#d8dee4",
      borderRadius: "50%",
      color: "#fff",
      padding: "0px",
      marginLeft: "-20px",
      marginTop: "5px",
      height: "18px",
      ":hover": {
        //backgroundColor: '#E2E8F0',
        // color: '#E53E3E',
      },
    }),
  };

  if (showLoader) {
    return (
      <Flex
        width="100%"
        height="100%"
        justifyContent="center"
        alignItems="center"
      >
        <ProgressCircle aria-label="Loading…" isIndeterminate />
      </Flex>
    );
  }

  const updateParentComponent = (contacts: Contact[]) => {
    const updateValue =
      contacts.length > 0
        ? new Set(
            contacts.map((item: any) => ({
              id: item.id,
              email: item.name,
              name: item.name,
              contactName: item.contactName,
              emailText: item.emailText,
            })),
          )
        : "";

    createParams(fieldName, updateValue);
  };

  // Convert contactsList.items to react-select format
  const options = contactsList.items.map((item: any) => ({
    value: item.id,
    label: `${item.contactName} <${item.name}>`,
    contactName: item.contactName,
    emailText: item.name,
  }));

  // Convert selectedContacts to react-select format
  const selectedValues =
    Array.isArray(selectedContacts) && selectedContacts.length
      ? selectedContacts.map((contact) => ({
          value: contact.id,
          label: contact.name,
          contactName: contact.contactName,
          emailText: contact.emailText,
        }))
      : [];

  const handleEditClick = (contact: Contact) => {
    setSelectedContacts(
      Array.isArray(selectedContacts)
        ? selectedContacts.map((c) =>
            c.id === contact.id
              ? { ...c, isEditing: true, tempName: c.emailText }
              : c,
          )
        : [],
    );
  };

  const handleEditKeyDown = (e: React.KeyboardEvent, contact: Contact) => {
    e.stopPropagation(); // Important - prevent react-select from capturing these events
    if (e.key === "Enter") {
      handleEditSave(contact);
    } else if (e.key === "Escape") {
      handleEditCancel(contact);
    }
  };

  const handleEditChange = (e: any, contact: Contact) => {
    setSelectedContacts((prev) =>
      prev.map((c) =>
        c.id === contact.id ? { ...c, tempName: e.target.value } : c,
      ),
    );
  };

  const handleEditSave = (contact: Contact): void => {
    if (!contact.tempName) {
      //ToastQueue.negative('Field cannot be empty', { timeout: 3000 });
      handleEditCancel(contact);
      //remove contact from selected contacts
      const filteredContacts = Array.isArray(selectedContacts)
        ? selectedContacts.filter((c) => c.id !== contact.id)
        : [];
      setSelectedContacts(filteredContacts);
      refSelectedContacts.current = filteredContacts;
      updateParentComponent(filteredContacts);
      return;
    }

    // Validate email format
    if (specificType === "email" && !isValidEmail(contact.tempName)) {
      ToastQueue.negative("Please enter a valid email address", {
        timeout: 3000,
      });
      return;
    }

    // Create updated contact with new email
    const updatedContact = {
      ...contact,
      name: `${contact.contactName} <${contact.tempName}>`,
      emailText: contact.tempName,
      isEditing: false,
      tempName: undefined,
    };

    // Create new contacts array with the updated contact
    const updatedContacts = Array.isArray(selectedContacts)
      ? selectedContacts.map((c) => (c.id === contact.id ? updatedContact : c))
      : [];

    // Update state
    setSelectedContacts(updatedContacts);

    // Update ref with the same data
    refSelectedContacts.current = updatedContacts;

    // Notify parent component about the change
    updateParentComponent(updatedContacts);
  };

  const handleEditCancel = (contact: Contact) => {
    setSelectedContacts((prev) =>
      Array.isArray(prev)
        ? prev.map((c) =>
            c.id === contact.id
              ? { ...c, isEditing: false, tempName: undefined }
              : c,
          )
        : [],
    );
  };

  return (
    <Flex direction="column" alignSelf="stretch" width="100%">
      <Flex
        direction="column"
        gap="size-100"
        alignSelf="stretch"
        width="100%"
        UNSAFE_style={{
          fontFamily: '"Adobe Clean", sans-serif',
          borderRadius: "4px",
          overflowX: "auto",
        }}
      >
        <CreatableSelect
          isMulti
          isClearable={false}
          isSearchable
          name="contacts"
          options={options}
          value={selectedValues}
          inputValue={inputValue}
          onInputChange={(value) => {
            setInputValue(value);
            //contactsList.setFilterText(value);
          }}
          onChange={handleChange}
          onCreateOption={handleCreate}
          placeholder={`
            ${t("search_or_add", {
              ns: "layout_components",
            })} ${specificType}...`}
          formatCreateLabel={(inputValue) =>
            `${t("add", { ns: "layout_components" })} "${inputValue}"`
          }
          noOptionsMessage={({ inputValue }) =>
            !inputValue
              ? `${t("type_to_search", {
                  ns: "layout_components",
                })} ${specificType}`
              : `${t("no_matching_found", {
                  ns: "layout_components",
                })} ${specificType}`
          }
          isLoading={contactsList.loadingState === "loading"}
          styles={{
            ...customStyles,
            // Add these to prevent focus issues
            multiValue: (base: any) => ({
              ...base,
              backgroundColor: "white",
              borderRadius: "4px",
              //padding: '0 0 0 4px', // Add padding for edit button
            }),
            // Ensure input container doesn't interfere
            valueContainer: (base: any) => ({
              ...base,
              //padding: '2px 8px',
            }),
          }}
          menuPosition="fixed"
          //formatOptionLabel={formatOptionLabel}
          components={{
            // Override the MultiValue component to handle editing
            MultiValue: (props: any) => {
              const contact = Array.isArray(selectedContacts)
                ? selectedContacts.find((c) => c.id === props.data.value)
                : undefined;
              //console.log('contact', contact);
              if (contact?.isEditing) {
                return (
                  <components.MultiValue {...props}>
                    <Flex alignItems={"center"}>
                      <input
                        type="text"
                        value={contact.tempName}
                        onChange={(e) => handleEditChange(e, contact)}
                        onKeyDown={(e) => handleEditKeyDown(e, contact)}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        className={styles.inputField}
                        autoFocus
                      />
                      {/* <button
                        onClick={e => {
                          e.stopPropagation();
                          handleEditSave(contact);
                        }}
                        className={`${styles.actionButton} ${styles.saveButton}`}
                        title="Save"
                      >
                        <i className="bi bi-check-circle-fill"></i>
                      </button>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          handleEditCancel(contact);
                        }}
                        className={`${styles.actionButton} ${styles.cancelButton}`}
                        title="Cancel"
                      >
                        <i className="bi bi-x-circle-fill"></i>
                      </button> */}
                    </Flex>
                  </components.MultiValue>
                );
              }

              // Regular non-editing tag - show only the name, not email
              const displayName = contact?.contactName || props.children;
              const fullText = contact?.emailText
                ? `${contact.contactName} <${contact.emailText}>`
                : displayName;
              return (
                <components.MultiValue {...props}>
                  <Flex alignItems={"center"}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditClick(contact);
                      }}
                      className={styles.tag}
                      title={fullText} // Show full email on hover
                    >
                      {truncateText(displayName, 40)}
                    </button>
                  </Flex>
                </components.MultiValue>
              );
            },
            // Don't include MultiValueLabel at all here
          }}
        />
      </Flex>
    </Flex>
  );
};

export default ContactsContactMultipleTag;
